import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { Account, AccountBalance, AccountBalanceInput, AccountInput, Statement } from '../types/finance';

/** Finanças → Contas (saldo calculado pelo backend: snapshot + lançamentos). */
class AccountService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /accounts?include_inactive=1 — com `balance_cents` (até hoje) e `projected_cents`. */
  async getAll(includeInactive = false): Promise<Account[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/accounts${toQuery({ include_inactive: includeInactive ? 1 : undefined })}`);
    return readList<Account>(response, 'Falha ao listar as contas');
  }

  /** GET /accounts/:id */
  async getById(id: number): Promise<Account> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/accounts/${id}`);
    return readData<Account>(response, 'Falha ao carregar a conta');
  }

  /** POST /accounts (409 nome repetido) */
  async create(input: AccountInput): Promise<Account> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/accounts', input);
    return readData<Account>(response, 'Falha ao criar a conta');
  }

  /** PUT /accounts/:id — `is_active` reativa/inativa. */
  async update(id: number, input: AccountInput): Promise<Account> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/accounts/${id}`, input);
    return readData<Account>(response, 'Falha ao atualizar a conta');
  }

  /** DELETE /accounts/:id — só inativa (não apaga lançamentos). */
  async deactivate(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/accounts/${id}`);
    await ensureOk(response, 'Falha ao inativar a conta');
  }

  /** GET /accounts/:id/statement?start=&end= — padrão: mês corrente. */
  async getStatement(id: number, start?: string, end?: string): Promise<Statement> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/accounts/${id}/statement${toQuery({ start, end })}`);
    const s = await readData<Statement>(response, 'Falha ao carregar o extrato');
    return { ...s, lines: s?.lines ?? [] };
  }

  /** GET /accounts/:id/balances — snapshots, mais recente primeiro. */
  async getBalances(id: number): Promise<AccountBalance[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/accounts/${id}/balances`);
    return readList<AccountBalance>(response, 'Falha ao listar os ajustes de saldo');
  }

  /** POST /accounts/:id/balances — upsert por data ("neste dia a conta fechou com R$ X"). */
  async setBalance(id: number, input: AccountBalanceInput): Promise<AccountBalance> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/accounts/${id}/balances`, input);
    return readData<AccountBalance>(response, 'Falha ao registrar o ajuste de saldo');
  }

  /** DELETE /accounts/:id/balances/:balanceId */
  async removeBalance(id: number, balanceId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/accounts/${id}/balances/${balanceId}`);
    await ensureOk(response, 'Falha ao excluir o ajuste de saldo');
  }
}

export const accountService = new AccountService();
export default accountService;
